import {
  PackageCabPricingRow,
  PackageCabType,
  PackageFaq,
  PackagePlace,
  PackageRouteLeg,
  TravelPackage,
  PackageTransferCalculation,
} from '../types/TravelPackage';

const CAB_MAX_OCCUPANCY: Record<PackageCabType, number> = {
  Sedan: 3,
  MUV: 6,
  'Innova Crysta': 6,
  '12 Seater Tempo Traveller': 12,
  '17 Seater Tempo Traveller': 17,
  'Force Urbania': 17,
};

export const PACKAGE_CAB_TYPES = Object.keys(CAB_MAX_OCCUPANCY) as PackageCabType[];

export const getCabMaxOccupancy = (cabType: PackageCabType): number => CAB_MAX_OCCUPANCY[cabType];

export const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
};

export const normalizePackageFaq = (value: unknown): PackageFaq[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const entry = item as PackageFaq;
      const question = String(entry.question || '').trim();
      const answer = String(entry.answer || '').trim();
      if (!question || !answer) return null;
      return { question, answer };
    })
    .filter((entry): entry is PackageFaq => Boolean(entry));
};

export const normalizePackagePlaces = (pkg?: Partial<TravelPackage> | null): PackagePlace[] => {
  const rawStops = Array.isArray(pkg?.stops) ? pkg?.stops ?? [] : [];
  if (rawStops.length > 0) {
    return rawStops
      .map((stop) => ({
        cityName: String(stop.cityName || stop.city_name || '').trim(),
        cityCode: String(stop.cityCode || stop.city_code || '').trim() || undefined,
        nights: Number(stop.nights || 0),
      }))
      .filter((stop) => stop.cityName && stop.nights > 0);
  }

  const pkgPlaces = pkg?.places;
  const rawPlaces: Array<PackagePlace | null | undefined> = Array.isArray(pkgPlaces) ? pkgPlaces : [];
  if (rawPlaces.length > 0) {
    const normalized: PackagePlace[] = [];
    rawPlaces.forEach((place) => {
      const cityName = String(place?.cityName || '').trim();
      const cityCode = String(place?.cityCode || '').trim();
      const nights = Number(place?.nights || 0);
      if (!cityName || nights <= 0) return;
      normalized.push({
        cityName,
        cityCode: cityCode || undefined,
        nights,
      });
    });
    return normalized;
  }

  const pkgCities = pkg?.cities;
  const rawCities: Array<string | Record<string, unknown>> = Array.isArray(pkgCities) ? pkgCities : [];
  if (rawCities.length === 0) return [];

  const totalNights = Number(pkg?.duration_nights || pkg?.durationNights || 0);
  const baseNights = rawCities.length > 0 ? Math.floor(totalNights / rawCities.length) : 0;
  const extraNights = rawCities.length > 0 ? totalNights % rawCities.length : 0;

  return rawCities
    .map((city, index) => {
      const cityName = typeof city === 'string'
        ? city
        : String((city as any)?.name || (city as any)?.city || (city as any)?.label || '').trim();
      if (!cityName) return null;
      return {
        cityName,
        nights: Math.max(1, baseNights + (index < extraNights ? 1 : 0)),
      };
    })
    .filter((place): place is PackagePlace => Boolean(place));
};

export const normalizePackageRouteLegs = (value: unknown): PackageRouteLeg[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const leg = item as Record<string, unknown>;
    const fromStopIndex = Number(leg.fromStopIndex ?? leg.from_stop_index);
    const toStopIndex = Number(leg.toStopIndex ?? leg.to_stop_index);
    if (!Number.isInteger(fromStopIndex) || !Number.isInteger(toStopIndex)) return [];
    const distanceKm = Number(leg.distanceKm ?? leg.distance_km);
    const durationMinutes = Number(leg.durationMinutes ?? leg.duration_minutes);
    const transferNote = String(leg.transferNote ?? leg.transfer_note ?? '').trim();
    const fromEndpoint = leg.fromEndpoint ?? leg.from_endpoint;
    const toEndpoint = leg.toEndpoint ?? leg.to_endpoint;
    return [{
      fromStopIndex,
      toStopIndex,
      ...(fromEndpoint === 'arrival_airport' ? { fromEndpoint } : {}),
      ...(toEndpoint === 'departure_airport' ? { toEndpoint } : {}),
      ...(Number.isFinite(distanceKm) && distanceKm >= 0 ? { distanceKm } : {}),
      ...(Number.isFinite(durationMinutes) && durationMinutes >= 0 ? { durationMinutes } : {}),
      ...(transferNote ? { transferNote } : {}),
    }];
  });
};

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value: unknown, fallback = false) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return Boolean(value);
};

const readCabPrice = (row: any): number => {
  const rawPrice = row?.price
    ?? row?.cabPrice
    ?? row?.cab_price
    ?? row?.priceAdjustment
    ?? row?.price_adjustment
    ?? row?.amount
    ?? row?.basePrice
    ?? row?.base_price;

  if (rawPrice !== undefined && rawPrice !== null && String(rawPrice).trim() !== '') {
    return Math.max(0, toNumber(rawPrice));
  }

  const rawPaise = row?.pricePaise
    ?? row?.price_paise
    ?? row?.cabPricePaise
    ?? row?.cab_price_paise
    ?? row?.amountPaise
    ?? row?.amount_paise;

  if (rawPaise !== undefined && rawPaise !== null && String(rawPaise).trim() !== '') {
    return Math.max(0, Math.round(toNumber(rawPaise) / 100));
  }

  return 0;
};

const readCabType = (value: any): PackageCabType => {
  const cabType = String(
    value?.cabType ??
    value?.cab_type ??
    value?.type ??
    'Sedan'
  ) as PackageCabType;
  return PACKAGE_CAB_TYPES.includes(cabType) ? cabType : 'Sedan';
};

export const normalizePackageCabPricing = (pkg?: Partial<TravelPackage> | null): PackageCabPricingRow[] => {
  const packageCabPricing = pkg?.packageCabPricing;
  const packageCabPricingSnake = pkg?.package_cab_pricing;
  const cabPricing = (pkg as (Partial<TravelPackage> & { cab_pricing?: Array<PackageCabPricingRow | Record<string, unknown>> }) | null | undefined)?.cab_pricing;
  const source: Array<PackageCabPricingRow | Record<string, unknown>> = Array.isArray(packageCabPricing)
    ? packageCabPricing
    : Array.isArray(packageCabPricingSnake)
      ? packageCabPricingSnake
      : Array.isArray(cabPricing)
        ? cabPricing
        : [];

  return source
    .map((rawRow, index) => {
      const row = rawRow as any;
      const cabType = readCabType(row);
      const id = row?.id ? String(row.id) : undefined;
      const tempId = row?.tempId ? String(row.tempId) : `row-${index + 1}`;
      return {
        id,
        tempId,
        cabType,
        maxOccupancy: getCabMaxOccupancy(cabType),
        price: readCabPrice(row),
        isActive: toBoolean(row?.isActive ?? row?.is_active ?? true, true),
        isDeleted: toBoolean(
          row?.isDeleted
          ?? row?.is_deleted
          ?? row?.deleted
          ?? row?.deleted_at
          ?? row?.deletedAt
          ?? false,
          false
        ),
        createdAt: row?.createdAt ?? row?.created_at,
        updatedAt: row?.updatedAt ?? row?.updated_at,
      };
    })
    .filter((row) => !row.isDeleted);
};

export const getMinimumTransferPrice = (rows: PackageCabPricingRow[]): number => {
  const candidates = rows
    .filter((row) => row.isActive && !row.isDeleted)
    .map((row) => Math.max(0, Number(row.price || 0)));

  if (candidates.length === 0) return 0;
  return Math.min(...candidates);
};

export const calculatePackageStartingPrice = (
  baseStartingPrice: number,
  rows: PackageCabPricingRow[]
): number => Math.max(0, Number(baseStartingPrice || 0)) + getMinimumTransferPrice(rows);

export const calculateQuotationTransfer = ({
  packageId,
  rows,
  pax,
  tripStartDate,
  overrideAmount,
  fallbackAmount,
  preferredSourceRowId,
  preferredCabType,
}: {
  packageId?: string;
  rows: PackageCabPricingRow[];
  pax: number;
  tripStartDate?: string;
  overrideAmount?: number | null;
  fallbackAmount?: number;
  preferredSourceRowId?: string;
  preferredCabType?: PackageCabType | string;
}): PackageTransferCalculation => {
  if (overrideAmount !== null && overrideAmount !== undefined && Number.isFinite(overrideAmount)) {
    return {
      packageId,
      pax,
      tripStartDate,
      price: Math.max(0, Number(overrideAmount || 0)),
      additionalCharge: 0,
      formula: 'flat',
      total: Math.max(0, Number(overrideAmount || 0)),
      isOverride: true,
      source: 'quotation_override',
    };
  }

  const activeRows = rows
    .filter((row) => row.isActive && !row.isDeleted)
    .slice();

  const preferredRow = activeRows.find((row) => {
    if (preferredSourceRowId && (row.id || row.tempId) === preferredSourceRowId) return true;
    if (preferredCabType && String(row.cabType || '').trim().toLowerCase() === String(preferredCabType || '').trim().toLowerCase()) return true;
    return false;
  });

  if (preferredRow) {
    const price = Math.max(0, Number(preferredRow.price || 0));
    return {
      packageId,
      cabType: preferredRow.cabType,
      maxOccupancy: preferredRow.maxOccupancy,
      pax: Math.max(1, pax),
      tripStartDate,
      price,
      additionalCharge: 0,
      formula: 'flat',
      total: price,
      sourceRowId: preferredRow.id || preferredRow.tempId,
      source: 'package_rule',
    };
  }

  const suitableRow = activeRows
    .filter((row) => row.maxOccupancy >= Math.max(1, pax))
    .sort((a, b) => a.maxOccupancy - b.maxOccupancy)[0];

  if (suitableRow) {
    const price = Math.max(0, Number(suitableRow.price || 0));
    return {
      packageId,
      cabType: suitableRow.cabType,
      maxOccupancy: suitableRow.maxOccupancy,
      pax: Math.max(1, pax),
      tripStartDate,
      price,
      additionalCharge: 0,
      formula: 'flat',
      total: price,
      sourceRowId: suitableRow.id || suitableRow.tempId,
      source: 'package_rule',
    };
  }

  if (Number(fallbackAmount || 0) > 0) {
    return {
      packageId,
      pax: Math.max(1, pax),
      tripStartDate,
      price: Math.max(0, Number(fallbackAmount || 0)),
      additionalCharge: 0,
      formula: 'flat',
      total: Math.max(0, Number(fallbackAmount || 0)),
      source: 'legacy_transfer',
    };
  }

  return {
    packageId,
    pax: Math.max(1, pax),
    tripStartDate,
    price: 0,
    additionalCharge: 0,
    formula: 'flat',
    total: 0,
    source: 'none',
  };
};
